import { useEffect, useRef, useState } from "react";
import {
  Sun, Cloud, CloudSun, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning, ChevronLeft, ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import WeatherDetailSheet from "@/components/WeatherDetailSheet";

/**
 * WeatherCarousel — horizontal auto-scrolling carousel of weather cards for
 * the 7 cities around Itapira. Data from backend /api/weather/cities (which
 * proxies Open-Meteo with a 30-min cache). Fails gracefully: if the backend
 * returns an empty list, the component renders nothing so the home screen
 * never breaks.
 */

const ICONS = {
  sun: Sun,
  cloud: Cloud,
  "cloud-sun": CloudSun,
  "cloud-fog": CloudFog,
  "cloud-drizzle": CloudDrizzle,
  "cloud-rain": CloudRain,
  "cloud-snow": CloudSnow,
  "cloud-lightning": CloudLightning,
};

const SCROLL_INTERVAL_MS = 4500;

const WeatherCarousel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const [selected, setSelected] = useState(null);
  const trackRef = useRef(null);
  const indexRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/weather/cities")
      .then((r) => {
        if (cancelled) return;
        setItems(r.data?.items || []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-scroll one card at a time, pause while user is touching/dragging
  useEffect(() => {
    if (!items.length) return;
    const timer = setInterval(() => {
      if (paused) return;
      const track = trackRef.current;
      if (!track) return;
      indexRef.current = (indexRef.current + 1) % items.length;
      const card = track.children[indexRef.current];
      if (card) {
        track.scrollTo({ left: card.offsetLeft - track.offsetLeft - 8, behavior: "smooth" });
      }
    }, SCROLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [items.length, paused]);

  const scrollBy = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[0];
    const step = (card?.getBoundingClientRect().width || 180) + 12;
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  // Graceful: nothing to show → render nothing (keeps home clean)
  if (!loading && items.length === 0) return null;

  return (
    <section className="mt-6" data-testid="weather-carousel">
      <div className="flex items-baseline justify-between mb-3 px-4 md:px-0">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold mb-0.5">
            Previsão do Tempo
          </div>
          <h2 className="font-display text-lg sm:text-xl font-black uppercase text-[var(--fi-blue)] leading-tight">
            Itapira e Região
          </h2>
        </div>
        <div className="hidden sm:flex gap-1">
          <button
            onClick={() => scrollBy(-1)}
            data-testid="weather-prev"
            aria-label="Anterior"
            className="w-8 h-8 rounded-full border border-gray-200 hover:border-[var(--fi-blue)] hover:text-[var(--fi-blue)] text-gray-500 flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => scrollBy(1)}
            data-testid="weather-next"
            aria-label="Próximo"
            className="w-8 h-8 rounded-full border border-gray-200 hover:border-[var(--fi-blue)] hover:text-[var(--fi-blue)] text-gray-500 flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        className="flex gap-3 overflow-x-auto scroll-smooth px-4 md:px-0 pb-2 snap-x snap-mandatory"
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setTimeout(() => setPaused(false), 2500)}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
      >
        {loading &&
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="shrink-0 w-40 h-32 rounded-2xl bg-white animate-pulse" />
          ))}
        {items.map((c) => {
          const Icon = ICONS[c.icon] || Cloud;
          return (
            <button
              type="button"
              key={c.id}
              onClick={() => setSelected(c.id)}
              data-testid={`weather-card-${c.id}`}
              aria-label={`Ver previsão completa de ${c.name}`}
              className="shrink-0 snap-start w-40 sm:w-44 text-left rounded-2xl bg-white p-4 shadow-[0_6px_20px_rgba(13,27,75,0.08)] border border-gray-100 hover:border-[var(--fi-blue)]/30 hover:-translate-y-0.5 active:scale-[0.98] transition-[transform,border-color] duration-200 cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-gray-500 font-bold truncate">
                    {c.name}
                  </div>
                  <div className="font-display text-3xl font-black text-[var(--fi-blue)] leading-none mt-0.5">
                    {c.temp}°
                  </div>
                </div>
                <div className="w-10 h-10 rounded-full bg-[var(--fi-blue)]/5 text-[var(--fi-blue)] flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-2 text-[11px] text-gray-500 truncate" title={c.label}>
                {c.label}
              </div>
              {(c.temp_min != null || c.temp_max != null) && (
                <div className="mt-2 flex items-center justify-between text-[11px] font-bold">
                  <div className="flex items-center gap-2">
                    <span className="text-[var(--fi-blue)]/60">
                      ↓ {c.temp_min ?? "—"}°
                    </span>
                    <span className="text-[var(--fi-red)]">
                      ↑ {c.temp_max ?? "—"}°
                    </span>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                </div>
              )}
            </button>
          );
        })}
      </div>
      <WeatherDetailSheet cityId={selected} onClose={() => setSelected(null)} />
    </section>
  );
};

export default WeatherCarousel;
